/**
 * Libellés des moyens de paiement — source unique pour l'impression et les aperçus.
 *
 * Avant, chaque chemin d'impression (orchestrateur ESC/POS, plugin Sunmi, aperçu
 * Dashboard) avait sa propre table, et `ticket_restaurant` manquait dans celle de
 * l'orchestrateur → la clé brute s'imprimait. Centralisé ici pour ne plus diverger.
 *
 * Les clés couvrent les valeurs stockées en base (`sales.payment_method` et les
 * entrées de `sales.split_payments`), avec et sans accent pour tolérer les deux
 * conventions historiques.
 */
export const PAYMENT_LABELS: Record<string, string> = {
  espèces: 'Espèces',
  especes: 'Espèces',
  carte: 'Carte bancaire',
  chèque: 'Chèque',
  cheque: 'Chèque',
  ticket_restaurant: 'Ticket restaurant',
  ticket_cadeau: 'Ticket cadeau',
  virement: 'Virement',
  multiple: 'Paiement fractionné',
};

/** Traduit une clé de moyen de paiement en libellé lisible (fallback = la clé). */
export function formatPaymentLabel(method: string | null | undefined): string {
  if (!method) return '';
  return PAYMENT_LABELS[method] ?? method;
}

export type SplitPaymentEntry = { method: string; amount: number };

/**
 * Lignes de paiement à imprimer sur un ticket (label + valeur cadrée à droite).
 * Fractionné → une ligne par moyen (libellé + montant) ; sinon la ligne
 * « Reglement » classique. Partagé par escpos-formatter et printer-adapters
 * pour éviter la double logique.
 */
export function paymentRows(data: {
  payment_method: string;
  split_payments?: SplitPaymentEntry[];
}): Array<{ label: string; value: string }> {
  if (data.split_payments && data.split_payments.length > 0) {
    return data.split_payments.map((sp) => ({
      label: formatPaymentLabel(sp.method),
      value: sp.amount.toFixed(2) + ' EUR',
    }));
  }
  return [{ label: 'Reglement', value: data.payment_method }];
}

/**
 * Normalise `sales.split_payments` (JSONB non typé) en entrées exploitables.
 * Renvoie `undefined` si absent/vide ou de forme inattendue.
 */
export function parseSplitPayments(raw: unknown): SplitPaymentEntry[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const entries = raw
    .map((e) => {
      const method = (e as { method?: unknown })?.method;
      const amount = Number((e as { amount?: unknown })?.amount);
      if (typeof method !== 'string' || !Number.isFinite(amount)) return null;
      return { method, amount };
    })
    .filter((e): e is SplitPaymentEntry => e !== null);
  return entries.length > 0 ? entries : undefined;
}
